import { addYears } from 'lib/datetime/dateUtils'

export interface MembershipYear {
  start: Date
  end: Date
}

/**
 * Calculate the current membership year boundaries.
 *
 * Rules (from tier-progression docs):
 * - Year 1: join date → last day of join month the following year
 * - Year 2+: 1st of month after join month → last day of join month
 *
 * When enrolmentDate is available, boundaries are calculated accurately.
 * Otherwise falls back to a statusReviewDate-based approximation.
 */
export function getCurrentMembershipYear(
  statusReviewDate: string,
  enrolmentDate?: string,
): MembershipYear {
  if (enrolmentDate) {
    const enrolment = new Date(enrolmentDate)
    const joinMonth = enrolment.getMonth()

    // Year 1 end: last day of join month in the year after enrolment
    // new Date(year, month+1, 0) gives the last day of month
    const year1End = new Date(enrolment.getFullYear() + 1, joinMonth + 1, 0)

    const now = new Date()

    // Still in Year 1
    if (now <= year1End) {
      return { start: enrolment, end: year1End }
    }

    // Year 2+: find which cycle we're in
    const cycleStartMonth = (joinMonth + 1) % 12

    let cycleStartYear = now.getFullYear()
    let cycleStart = new Date(cycleStartYear, cycleStartMonth, 1)
    if (cycleStart > now) {
      cycleStartYear -= 1
      cycleStart = new Date(cycleStartYear, cycleStartMonth, 1)
    }

    // Cycle end: last day of join month
    const cycleEndYear = cycleStartMonth <= joinMonth ? cycleStartYear : cycleStartYear + 1
    const cycleEnd = new Date(cycleEndYear, joinMonth + 1, 0)

    return { start: cycleStart, end: cycleEnd }
  }

  // Fallback: approximate from statusReviewDate (assumes it's the current year end)
  const reviewDate = new Date(statusReviewDate)
  const start = new Date(reviewDate.getFullYear() - 1, reviewDate.getMonth() + 1, 1)
  // End = last day of review month
  const end = new Date(reviewDate.getFullYear(), reviewDate.getMonth() + 1, 0)
  return { start, end }
}

/**
 * Returns the next tier review date (1 year after current membership year end).
 */
export function getNextReviewDate(membershipYearEnd: Date): Date {
  return addYears(membershipYearEnd, 1)
}

export interface TierChange {
  date: string
  description: string
  /** The tier that was unlocked, parsed from the activity description. Falls back to undefined if unparseable. */
  unlockedTierName: string | undefined
}

/**
 * Extracts the target tier name from a tier_change activity description.
 * Expected format: "Tier upgraded from Bronze to Silver"
 */
function parseUnlockedTierName(description: string | undefined): string | undefined {
  if (!description) return undefined
  const match = description.match(/to\s+(\w+)\s*$/i)
  return match?.[1]
}

/**
 * Returns all tier_change activities within the given membership year, sorted chronologically.
 */
export function getTierChangesInMembershipYear(
  activities: Array<App.LoyaltyAccountActivity>,
  membershipYear: MembershipYear,
): Array<TierChange> {
  return activities
    .filter(activity => {
      if (activity.type !== 'tier_change') return false
      const activityDate = new Date(activity.date)
      return activityDate >= membershipYear.start && activityDate <= membershipYear.end
    })
    .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())
    .map(activity => ({
      date: activity.date,
      description: activity.description ?? '',
      unlockedTierName: parseUnlockedTierName(activity.description),
    }))
}
